// Admin Backup Tab Component
import React, { useState, useRef } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Archive, Download, RotateCcw, Trash2, Upload, FileUp, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { useAdmin, API_URL } from './AdminContext';

export function AdminBackupTab() {
  const { backups, fetchBackups, fetchAll } = useAdmin();
  const [backupType, setBackupType] = useState('all');
  const [includeSystem, setIncludeSystem] = useState(true);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [restoreSystem, setRestoreSystem] = useState(true);
  const fileInputRef = useRef(null);

  const handleCreateBackup = async () => {
    try {
      const response = await fetch(`${API_URL}/api/backup/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ store_locally: true, include_system: includeSystem })
      });
      if (response.ok) {
        toast.success('Backup created and stored');
        fetchBackups();
      } else {
        toast.error('Failed to create backup');
      }
    } catch (error) {
      toast.error('Failed to create backup');
    }
  };

  const handleDownloadBackup = async () => {
    try {
      const response = await fetch(`${API_URL}/api/backup/download-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ include_system: includeSystem })
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `backup_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        toast.success('Backup downloaded');
      }
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  const handleRestoreBackup = async (backupId) => {
    if (!confirm('Restore this backup? This will overwrite existing data.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/backup/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ backup_id: backupId, merge: false })
      });
      if (response.ok) {
        toast.success('Backup restored');
        fetchAll();
      }
    } catch (error) {
      toast.error('Failed to restore backup');
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!confirm('Delete this backup?')) return;
    
    await fetch(`${API_URL}/api/backup/${backupId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    fetchBackups();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON backup file');
      return;
    }
    
    try {
      const content = await file.text();
      const backupData = JSON.parse(content);
      
      // Validate backup structure
      if (!backupData.backup_id || (!backupData.tenants && !backupData.system_data)) {
        toast.error('Invalid backup file format');
        return;
      }
      
      setSelectedFile(file);
      setRestorePreview({
        backup_id: backupData.backup_id,
        created_at: backupData.created_at,
        tenant_count: backupData.tenants?.length || 0,
        has_system_data: !!backupData.system_data,
        data: backupData
      });
      setRestoreDialogOpen(true);
    } catch (error) {
      toast.error('Failed to read backup file');
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRestoreFromFile = async () => {
    if (!restorePreview?.data) return;
    
    setRestoring(true);
    try {
      const response = await fetch(`${API_URL}/api/backup/restore-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          backup_data: restorePreview.data,
          merge: mergeMode,
          restore_system: restoreSystem
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Restored ${result.restored_tenants?.length || 0} tenants${result.restored_system ? ' and system settings' : ''}`);
        setRestoreDialogOpen(false);
        setRestorePreview(null);
        fetchAll();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to restore backup');
      }
    } catch (error) {
      toast.error('Failed to restore backup');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="space-y-4">
      <GroupBox title="Create Backup" className="border-green-500/50">
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Backup Type</Label>
              <Select value={backupType} onValueChange={setBackupType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  <SelectItem value="selected">Selected Tenants</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch 
                id="includeSystem" 
                checked={includeSystem}
                onCheckedChange={setIncludeSystem}
              />
              <Label htmlFor="includeSystem" className="text-sm">Include All System Settings & Plans</Label>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreateBackup}>
              <Archive className="w-4 h-4 mr-2" />
              Create & Store Backup
            </Button>
            <Button variant="outline" onClick={handleDownloadBackup}>
              <Download className="w-4 h-4 mr-2" />
              Download Directly
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {includeSystem 
              ? "Backup includes: All tenants, users, transactions, platform settings, subscription plans, payment configs, and system data"
              : "Backup includes: All tenants, users, and transactions only"}
          </p>
        </div>
      </GroupBox>

      <GroupBox title="Restore from File" className="border-amber-500/50">
        <div className="p-4">
          <input 
            type="file"
            ref={fileInputRef}
            accept=".json"
            onChange={handleFileSelect}
            className="hidden"
          />
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
              <Upload className="w-4 h-4 mr-2" />
              Select Backup File (.json)
            </Button>
            <p className="text-sm text-muted-foreground">
              Upload a previously downloaded backup file to restore
            </p>
          </div>
        </div>
      </GroupBox>

      <GroupBox title="Stored Backups" className="border-blue-500/50">
        <div className="p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2">Date</th>
                <th className="text-left py-2">Type</th>
                <th className="text-center py-2">Tenants</th>
                <th className="text-center py-2">System</th>
                <th className="text-right py-2">Size</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((backup) => (
                <tr key={backup.backup_id} className="border-b border-gray-800">
                  <td className="py-2">{new Date(backup.created_at).toLocaleString()}</td>
                  <td className="py-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${backup.type === 'full' ? 'bg-green-900/30 text-green-400' : 'bg-blue-900/30 text-blue-400'}`}>
                      {backup.type}
                    </span>
                  </td>
                  <td className="py-2 text-center">{backup.tenant_count}</td>
                  <td className="py-2 text-center">
                    {backup.include_system ? (
                      <span className="text-green-400">Yes</span>
                    ) : (
                      <span className="text-gray-500">No</span>
                    )}
                  </td>
                  <td className="py-2 text-right">{(backup.file_size / 1024).toFixed(1)} KB</td>
                  <td className="py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => {
                        window.open(`${API_URL}/api/backup/download/${backup.backup_id}`, '_blank');
                      }} title="Download">
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleRestoreBackup(backup.backup_id)} title="Restore">
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeleteBackup(backup.backup_id)} title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {backups.length === 0 && (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500">No backups found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </GroupBox>

      {/* Restore Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5" />
              Restore Backup
            </DialogTitle>
            <DialogDescription>
              Review the backup details before restoring
            </DialogDescription>
          </DialogHeader>
          
          {restorePreview && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Backup ID:</span>
                  <span className="font-mono text-sm">{restorePreview.backup_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created:</span>
                  <span>{new Date(restorePreview.created_at).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tenants:</span>
                  <span>{restorePreview.tenant_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">System Data:</span>
                  <span className={restorePreview.has_system_data ? 'text-green-400' : 'text-gray-500'}>
                    {restorePreview.has_system_data ? 'Included' : 'Not included'}
                  </span>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Switch 
                    id="mergeMode"
                    checked={mergeMode}
                    onCheckedChange={setMergeMode}
                  />
                  <Label htmlFor="mergeMode">Merge mode (don't delete existing data)</Label>
                </div>
                
                {restorePreview.has_system_data && (
                  <div className="flex items-center gap-3">
                    <Switch 
                      id="restoreSystem"
                      checked={restoreSystem}
                      onCheckedChange={setRestoreSystem}
                    />
                    <Label htmlFor="restoreSystem">Restore system settings & plans</Label>
                  </div>
                )}
              </div>
              
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-200">
                  {mergeMode 
                    ? "Merge mode will add data without deleting existing records."
                    : "This will overwrite existing data. Make sure you have a current backup first."}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRestoreFromFile} disabled={restoring}>
              {restoring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restoring...
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Restore Backup
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
